import React, { useState, useEffect, useRef } from "react";

const Particle = ({ style }) => <div style={style} />;

const DefaultFailure = () => {
    const [paymentData, setPaymentData] = useState({
        payment_id: "",
        status: "",
        customer_email: "",
        amount: "",
        currency: ""
    });
    const [visible, setVisible] = useState(false);
    const [shaking, setShaking] = useState(false);
    const [particles, setParticles] = useState([]);
    const animFrameRef = useRef(null);

    useEffect(() => {
        const urlParams = new URLSearchParams(window.location.search);
        setPaymentData({
            payment_id: urlParams.get("payment_id") || "",
            status: urlParams.get("status") || "failed",
            customer_email: urlParams.get("customer_email") || "",
            amount: urlParams.get("amount") || "",
            currency: urlParams.get("currency") || "USD"
        });

        // Trigger entrance animation
        setTimeout(() => setVisible(true), 50);

        // Trigger shake after entrance
        setTimeout(() => {
            setShaking(true);
            setTimeout(() => setShaking(false), 600);
        }, 700);

        // Generate burst particles
        const generated = Array.from({ length: 18 }, (_, i) => ({
            id: i,
            angle: (i / 18) * 360,
            distance: 80 + Math.random() * 60,
            size: 4 + Math.random() * 6,
            color: i % 3 === 0 ? "#ef4444" : i % 3 === 1 ? "#fca5a5" : "#fee2e2",
            duration: 0.6 + Math.random() * 0.4,
            delay: 0.7 + Math.random() * 0.15,
        }));
        setParticles(generated);

        return () => cancelAnimationFrame(animFrameRef.current);
    }, []);

    const handleTryAgain = () => window.history.back();
    const handleContactSupport = () => { window.location.href = "/support"; };

    const css = `
    @import url('https://fonts.googleapis.com/css2?family=Roboto:wght@400;500;600;700&display=swap');

    @keyframes slideUp {
      0%   { opacity:0; transform: translateY(40px) scale(0.97); }
      100% { opacity:1; transform: translateY(0) scale(1); }
    }
    @keyframes shake {
      0%,100% { transform: translateX(0); }
      15%     { transform: translateX(-8px) rotate(-1deg); }
      30%     { transform: translateX(8px)  rotate(1deg); }
      45%     { transform: translateX(-6px); }
      60%     { transform: translateX(6px); }
      75%     { transform: translateX(-3px); }
      90%     { transform: translateX(3px); }
    }
    @keyframes popIn {
      0%   { opacity:0; transform: scale(0.4) rotate(-15deg); }
      60%  { transform: scale(1.2) rotate(5deg); }
      80%  { transform: scale(0.95) rotate(-2deg); }
      100% { opacity:1; transform: scale(1) rotate(0deg); }
    }
    @keyframes barGrow {
      from { transform: scaleX(0); }
      to   { transform: scaleX(1); }
    }
    @keyframes pulse {
      0%,100% { box-shadow: 0 0 0 0 rgba(239,68,68,0.35); }
      50%     { box-shadow: 0 0 0 16px rgba(239,68,68,0); }
    }
    @keyframes fadeSlide {
      from { opacity:0; transform: translateX(-12px); }
      to   { opacity:1; transform: translateX(0); }
    }
    @keyframes particleBurst {
      0%   { opacity:1; transform: translate(0,0) scale(1); }
      100% { opacity:0; transform: translate(var(--tx), var(--ty)) scale(0); }
    }
    @keyframes scanline {
      0%   { top: -20%; }
      100% { top: 110%; }
    }
    @keyframes ripple {
      0%   { transform: scale(0.8); opacity:0.6; }
      100% { transform: scale(2.2); opacity:0; }
    }

    .df-card {
      animation: slideUp 0.55s cubic-bezier(0.34,1.56,0.64,1) both;
    }
    .df-card.shake {
      animation: shake 0.55s cubic-bezier(0.36,0.07,0.19,0.97) both;
    }
    .df-icon-wrap {
      animation: popIn 0.5s cubic-bezier(0.34,1.56,0.64,1) 0.45s both;
    }
    .df-bar {
      transform-origin: left;
      animation: barGrow 0.9s cubic-bezier(0.22,1,0.36,1) 0.2s both;
    }
    .df-btn-primary {
      cursor: pointer;
      transition: transform 0.2s, box-shadow 0.2s;
    }
    .df-btn-primary:hover {
      transform: translateY(-3px) scale(1.02);
      box-shadow: 0 12px 32px rgba(239,68,68,0.45) !important;
    }
    .df-btn-primary:active {
      transform: scale(0.97);
    }
    .df-btn-secondary {
      cursor: pointer;
      transition: transform 0.2s, background 0.2s, border-color 0.2s;
    }
    .df-btn-secondary:hover {
      transform: translateY(-3px);
      background: #fef2f2 !important;
      border-color: #fca5a5 !important;
    }
    .df-btn-secondary:active {
      transform: scale(0.97);
    }
    .df-detail-row {
      animation: fadeSlide 0.4s ease both;
    }
    .df-pulse {
      animation: pulse 2s ease-in-out 1.2s infinite;
    }
    .df-ripple {
      animation: ripple 1.8s ease-out 0.6s infinite;
    }
    .df-scanline {
      animation: scanline 3s linear infinite;
    }
    @media (max-width: 480px) {
      .df-detail-row {
        flex-direction: column;
        align-items: flex-start;
        gap: 4px;
      }
    }
    `;

    const radToDeg = (a) => a;
    const particleStyle = (p) => {
        const rad = (p.angle * Math.PI) / 180;
        return {
            position: "absolute",
            top: "50%",
            left: "50%",
            width: `${p.size}px`,
            height: `${p.size}px`,
            borderRadius: "50%",
            background: p.color,
            "--tx": `${Math.cos(rad) * p.distance}px`,
            "--ty": `${Math.sin(rad) * p.distance}px`,
            animation: `particleBurst ${p.duration}s ease-out ${p.delay}s both`,
            pointerEvents: "none",
            marginLeft: `-${p.size / 2}px`,
            marginTop: `-${p.size / 2}px`,
        };
    };

    const hasDetails = paymentData.payment_id || paymentData.amount || paymentData.customer_email;

    return (
        <>
            <style>{css}</style>
            <div style={{
                minHeight: "100vh",
                background: "linear-gradient(135deg, #fff1f2 0%, #fef2f2 40%, #fff5f5 70%, #ffe4e6 100%)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                padding: "20px",
                fontFamily: "'Roboto', sans-serif",
                position: "relative",
                overflow: "hidden",
            }}>
                {/* Decorative BG circles */}
                <div style={{ position: "absolute", top: "-80px", right: "-80px", width: "340px", height: "340px", borderRadius: "50%", background: "rgba(239,68,68,0.06)", pointerEvents: "none" }} />
                <div style={{ position: "absolute", bottom: "-60px", left: "-60px", width: "260px", height: "260px", borderRadius: "50%", background: "rgba(239,68,68,0.05)", pointerEvents: "none" }} />
                <div style={{ position: "absolute", top: "30%", left: "8%", width: "80px", height: "80px", borderRadius: "50%", background: "rgba(239,68,68,0.04)", pointerEvents: "none" }} />

                <div
                    className={`df-card${shaking ? " shake" : ""}`}
                    style={{
                        maxWidth: "480px",
                        width: "100%",
                        background: "#ffffff",
                        borderRadius: "20px",
                        boxShadow: "0 20px 60px rgba(239,68,68,0.12), 0 4px 20px rgba(0,0,0,0.06)",
                        overflow: "hidden",
                        border: "1px solid rgba(239,68,68,0.12)",
                        position: "relative",
                    }}
                >
                    {/* Animated scanline shimmer */}
                    <div style={{ position: "absolute", inset: 0, overflow: "hidden", borderRadius: "20px", pointerEvents: "none", zIndex: 0 }}>
                        <div className="df-scanline" style={{
                            position: "absolute", left: 0, right: 0, height: "60px",
                            background: "linear-gradient(180deg,transparent,rgba(239,68,68,0.04),transparent)",
                        }} />
                    </div>

                    {/* Top accent bar */}
                    <div className="df-bar" style={{
                        height: "5px",
                        background: "linear-gradient(90deg, #ef4444 0%, #f97316 50%, #ef4444 100%)",
                        backgroundSize: "200% 100%",
                        animation: "barGrow 0.9s cubic-bezier(0.22,1,0.36,1) 0.2s both",
                        position: "relative", zIndex: 1,
                    }} />

                    <div style={{ padding: "40px 36px 36px", textAlign: "center", position: "relative", zIndex: 1 }}>
                        {/* Icon with ripple + burst */}
                        <div style={{ position: "relative", display: "inline-block", marginBottom: "24px" }}>
                            {/* Ripple rings */}
                            {[0, 1].map(i => (
                                <div key={i} className="df-ripple" style={{
                                    position: "absolute", inset: "-10px", borderRadius: "50%",
                                    border: "2px solid rgba(239,68,68,0.3)",
                                    animationDelay: `${0.6 + i * 0.6}s`,
                                }} />
                            ))}
                            {/* Particles */}
                            {particles.map(p => (
                                <div key={p.id} style={particleStyle(p)} />
                            ))}
                            {/* Icon circle */}
                            <div className="df-icon-wrap df-pulse" style={{
                                width: "80px", height: "80px", borderRadius: "50%",
                                background: "linear-gradient(135deg,#fee2e2,#fecaca)",
                                display: "inline-flex", alignItems: "center", justifyContent: "center",
                                border: "2px solid rgba(239,68,68,0.2)",
                                position: "relative",
                            }}>
                                <svg xmlns="http://www.w3.org/2000/svg" width="38" height="38"
                                    viewBox="0 0 24 24" fill="none" stroke="#ef4444"
                                    strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                                    <path d="M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" />
                                    <line x1="12" y1="9" x2="12" y2="13" />
                                    <line x1="12" y1="17" x2="12.01" y2="17" />
                                </svg>
                            </div>
                        </div>

                        {/* Title */}
                        <h1 style={{
                            fontSize: "30px", fontWeight: "700", color: "#111827",
                            marginBottom: "10px", letterSpacing: "-0.6px", lineHeight: "1.2",
                        }}>
                            Payment Failed
                        </h1>
                        <p style={{
                            fontSize: "15px", color: "#6b7280", marginBottom: "28px", lineHeight: "1.65",
                        }}>
                            We couldn't process your payment.<br />Please try again or contact our support team.
                        </p>

                        {/* Details card */}
                        {hasDetails && (
                            <div style={{
                                background: "linear-gradient(135deg,#fff5f5,#fef2f2)",
                                borderRadius: "14px", padding: "4px 0", marginBottom: "28px",
                                textAlign: "left", border: "1px solid rgba(239,68,68,0.1)",
                                overflow: "hidden",
                            }}>
                                {paymentData.payment_id && (
                                    <div className="df-detail-row" style={{
                                        display: "flex", justifyContent: "space-between", alignItems: "center",
                                        padding: "13px 20px", borderBottom: "1px solid rgba(239,68,68,0.08)",
                                        animationDelay: "0.8s",
                                    }}>
                                        <span style={{ fontSize: "13px", fontWeight: "500", color: "#9ca3af", letterSpacing: "0.3px" }}>Payment ID</span>
                                        <span style={{ fontSize: "13px", fontWeight: "600", color: "#374151", fontFamily: "monospace", background: "#fff", padding: "2px 8px", borderRadius: "6px", border: "1px solid #e5e7eb", wordBreak: "break-all" }}>{paymentData.payment_id}</span>
                                    </div>
                                )}
                                {paymentData.amount && (
                                    <div className="df-detail-row" style={{
                                        display: "flex", justifyContent: "space-between", alignItems: "center",
                                        padding: "13px 20px", borderBottom: paymentData.customer_email ? "1px solid rgba(239,68,68,0.08)" : "none",
                                        animationDelay: "0.9s",
                                    }}>
                                        <span style={{ fontSize: "13px", fontWeight: "500", color: "#9ca3af" }}>Amount</span>
                                        <span style={{ fontSize: "15px", fontWeight: "700", color: "#ef4444" }}>{paymentData.amount} {paymentData.currency}</span>
                                    </div>
                                )}
                                {paymentData.customer_email && (
                                    <div className="df-detail-row" style={{
                                        display: "flex", justifyContent: "space-between", alignItems: "center",
                                        padding: "13px 20px", animationDelay: "1s",
                                    }}>
                                        <span style={{ fontSize: "13px", fontWeight: "500", color: "#9ca3af" }}>Email</span>
                                        <span style={{ fontSize: "13px", fontWeight: "600", color: "#374151" }}>{paymentData.customer_email}</span>
                                    </div>
                                )}
                            </div>
                        )}

                        {/* Buttons */}
                        <div style={{ display: "flex", gap: "12px", marginBottom: "20px" }}>
                            <button
                                className="df-btn-primary"
                                onClick={handleTryAgain}
                                style={{
                                    flex: 1, padding: "14px 20px",
                                    background: "linear-gradient(135deg,#ef4444,#dc2626)",
                                    color: "#fff", border: "none", borderRadius: "12px",
                                    fontSize: "14px", fontWeight: "600",
                                    boxShadow: "0 4px 16px rgba(239,68,68,0.30)",
                                    display: "flex", alignItems: "center", justifyContent: "center", gap: "8px",
                                }}
                            >
                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                    <polyline points="1 4 1 10 7 10" />
                                    <path d="M3.51 15a9 9 0 102.13-9.36L1 10" />
                                </svg>
                                Try Again
                            </button>
                            <button
                                className="df-btn-secondary"
                                onClick={handleContactSupport}
                                style={{
                                    flex: 1, padding: "14px 20px",
                                    background: "#fff", color: "#6b7280",
                                    border: "1.5px solid #e5e7eb", borderRadius: "12px",
                                    fontSize: "14px", fontWeight: "600",
                                    display: "flex", alignItems: "center", justifyContent: "center", gap: "8px",
                                }}
                            >
                                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                    <path d="M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z" />
                                </svg>
                                Get Help
                            </button>
                        </div>

                        {/* Support note */}
                        <p style={{ fontSize: "12px", color: "#9ca3af", lineHeight: "1.6", marginBottom: "16px" }}>
                            If you were charged but didn't receive confirmation,<br />please contact our support team immediately.
                        </p>

                        {/* Footer */}
                        <div style={{
                            display: "flex", alignItems: "center", justifyContent: "center", gap: "6px",
                            paddingTop: "16px", borderTop: "1px solid #f3f4f6",
                        }}>
                            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#9ca3af" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
                                <path d="M7 11V7a5 5 0 0110 0v4" />
                            </svg>
                            <span style={{ fontSize: "12px", color: "#9ca3af" }}>Secure payment processed by PayBito Pro</span>
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
};

export default DefaultFailure;